/**
 * Copyright 2024-2026 Tesenso AG
 *
 * Rollout Project Models
 *
 * Data models for the Rollout Planning & Procurement feature.
 */
import { MeterStatus } from '../../mkz-editor-v2/models/mkz-editor-v2.models';

// =========================================================
// ROLLOUT PROJECT
// =========================================================

export interface RolloutProject {
  id?: { id: string };
  tenantId: { id: string };
  customerId?: { id: string };
  name: string;
  description?: string;
  status: RolloutProjectStatus;
  portfolioId?: string;
  mkzEditorIds: string[];
  propertyMappings: Record<string, string>;  // mkzEditorId -> portfolioPropertyId
  targetStartDate?: number;
  targetCompletionDate?: number;
  projectManager?: string;
  additionalInfo?: any;
  createdTime?: number;
}

export enum RolloutProjectStatus {
  PLANNING = 'PLANNING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  ON_HOLD = 'ON_HOLD'
}

export const ROLLOUT_STATUS_LABELS: Record<RolloutProjectStatus, string> = {
  [RolloutProjectStatus.PLANNING]: 'Planung',
  [RolloutProjectStatus.IN_PROGRESS]: 'In Bearbeitung',
  [RolloutProjectStatus.COMPLETED]: 'Abgeschlossen',
  [RolloutProjectStatus.ON_HOLD]: 'Pausiert'
};

export const ROLLOUT_STATUS_COLORS: Record<RolloutProjectStatus, string> = {
  [RolloutProjectStatus.PLANNING]: '#9E9E9E',
  [RolloutProjectStatus.IN_PROGRESS]: '#2196F3',
  [RolloutProjectStatus.COMPLETED]: '#4CAF50',
  [RolloutProjectStatus.ON_HOLD]: '#FF9800'
};

// =========================================================
// ROLLOUT PROGRESS
// =========================================================

export interface RolloutProgress {
  projectId: string;
  projectName: string;
  overallProgress: number;        // 0-100% (based on COMMISSIONED meters)
  totalMeters: number;
  metersByStatus: Record<string, number>;
  buildings: BuildingProgress[];
}

export interface BuildingProgress {
  mkzEditorId: string;
  buildingName: string;
  address?: string;
  totalMeters: number;
  metersByStatus: Record<string, number>;
  progress: number;               // 0-100%
  inspectionCompleted: boolean;
  mkzApproved: boolean;
  nextAction: string;
}

// =========================================================
// PROCUREMENT SUMMARY
// =========================================================

export interface ProcurementSummary {
  projectId: string;
  totalMeters: number;
  metersByStatus: Record<string, number>;
  supplierGroups: ProcurementSupplierGroup[];
  totalEstimatedCost: number;
}

export interface ProcurementSupplierGroup {
  supplierName: string;
  products: ProcurementProductLine[];
  subtotal: number;
}

export interface ProcurementProductLine {
  productId: string;
  productName: string;
  productSku: string;
  unitPrice: number;
  totalQuantityNeeded: number;
  quantityOrdered: number;
  quantityDelivered: number;
  quantityRemaining: number;
  stockAvailable: number;
  supplierLeadTimeDays: number;
  buildingBreakdown: BuildingProductBreakdown[];
}

export interface BuildingProductBreakdown {
  mkzEditorId: string;
  buildingName: string;
  quantity: number;
}

// =========================================================
// ADD BUILDING REQUEST
// =========================================================

export interface AddBuildingRequest {
  mkzEditorId: string;
  portfolioPropertyId?: string;
}

// =========================================================
// FLAT METER LIST (per-device rollout management)
// =========================================================

export interface RolloutMeterProduct {
  productId: string;
  productName: string;
  productSku: string;
  quantity: number;
  supplier?: string;
  orderId?: string;
  orderStatus?: string;     // DRAFT, CONFIRMED, PROCESSING, SHIPPED, DELIVERED, ...
  deliveryDate?: number;
}

export interface RolloutMeter {
  meterId: string;
  mkzEditorId: string;
  buildingName: string;
  address?: string;
  name: string;
  mediumType: string;
  meterType?: string;
  meteringLevel?: string;
  meterNumber?: string;
  manufacturer?: string;
  model?: string;
  location?: string;
  aufschaltMethode?: string;
  deviceId?: string;
  deviceName?: string;
  responsiblePerson?: string;
  plannedInstallationDate?: number;
  isVirtual: boolean;
  meterStatus: MeterStatus | string;
  pipelineIndex: number;    // index in ROLLOUT_PIPELINE, -1 for special statuses
  requiredProducts: RolloutMeterProduct[];
  // Scoping (filters: Portfolio / Customer / Gebäude)
  customerId?: string;
  customerName?: string;
  portfolioPropertyId?: string;
  portfolioId?: string;
  portfolioName?: string;
  // Assigned partner work order (coordination)
  workOrder?: MeterWorkOrderInfo;
  // Inspection photos (situation on site, from Begehung)
  photoIds?: string[];
}

/**
 * Reduced meter view for partners (work order / RFQ scope of work).
 */
export interface PartnerMeterInfo {
  meterId: string;
  name: string;
  buildingName?: string;
  address?: string;
  location?: string;
  meterNumber?: string;
  manufacturer?: string;
  model?: string;
  mediumType?: string;
  aufschaltMethode?: string;
  meterStatus?: string;
  photoIds?: string[];
}

export interface RolloutMetersResponse {
  projectId: string;
  totalMeters: number;
  metersByStatus: Record<string, number>;
  pipeline: string[];
  meters: RolloutMeter[];
}

export interface BulkMeterStatusRequest {
  meterIds: string[];
  newStatus: string;
  note?: string;
}

export interface BulkMeterStatusResult {
  requested: number;
  updated: number;
  newStatus: string;
}

// =========================================================
// AUFSCHALT PRODUCT RULES (A1: auto material assignment)
// =========================================================

export enum QuantityBasis {
  PER_METER = 'PER_METER',
  PER_N_METERS = 'PER_N_METERS',
  PER_BUILDING = 'PER_BUILDING'
}

export const QUANTITY_BASIS_LABELS: Record<QuantityBasis, string> = {
  [QuantityBasis.PER_METER]: 'Pro Zähler',
  [QuantityBasis.PER_N_METERS]: 'Pro N Zähler',
  [QuantityBasis.PER_BUILDING]: 'Pro Gebäude'
};

export interface AufschaltProductRule {
  id?: string;
  createdTime?: number;
  tenantId?: string;
  aufschaltMethode: string;
  productId: string;
  productName?: string;
  productSku?: string;
  supplier?: string;
  quantityBasis: QuantityBasis;
  quantity: number;
  groupSize?: number;
  purpose: string;
  sortOrder?: number;
  notes?: string;
}

export interface AutoAssignResult {
  metersUpdated: number;
  productsAssigned: number;
  skippedNoMethode: number;
  skippedNoRule: number;
  skippedHasProducts: number;
}

// =========================================================
// SUPPLIERS & PURCHASE ORDERS (A2)
// =========================================================

export enum PartnerType {
  SUPPLIER = 'SUPPLIER',
  INSTALLER = 'INSTALLER',
  BOTH = 'BOTH'
}

export const PARTNER_TYPE_LABELS: Record<PartnerType, string> = {
  [PartnerType.SUPPLIER]: 'Lieferant',
  [PartnerType.INSTALLER]: 'Installationspartner',
  [PartnerType.BOTH]: 'Lieferant + Installation'
};

export const SERVICE_TYPES = ['ELEKTRO', 'HLK', 'SANITAER', 'MESSTECHNIK', 'ABLESEDIENST', 'SONSTIGES'] as const;

export const SERVICE_TYPE_LABELS: Record<string, string> = {
  ELEKTRO: 'Elektro',
  HLK: 'HLK / Heizung',
  SANITAER: 'Sanitär',
  MESSTECHNIK: 'Messtechnik',
  ABLESEDIENST: 'Ablesedienst',
  SONSTIGES: 'Sonstiges'
};

export interface Supplier {
  id?: string;
  createdTime?: number;
  tenantId?: string;
  name: string;
  email?: string;
  phone?: string;
  contactPerson?: string;
  address?: string;
  notes?: string;
  partnerType?: PartnerType;
  customerId?: string;       // TB customer link -> partner portal access
  serviceTypes?: string[];
}

// =========================================================
// WORK ORDERS (partner coordination)
// =========================================================

export enum WorkOrderStatus {
  OPEN = 'OPEN',
  SCHEDULED = 'SCHEDULED',
  IN_PROGRESS = 'IN_PROGRESS',
  DONE = 'DONE',
  CANCELLED = 'CANCELLED'
}

export const WORK_ORDER_STATUS_LABELS: Record<WorkOrderStatus, string> = {
  [WorkOrderStatus.OPEN]: 'Offen',
  [WorkOrderStatus.SCHEDULED]: 'Terminiert',
  [WorkOrderStatus.IN_PROGRESS]: 'In Arbeit',
  [WorkOrderStatus.DONE]: 'Erledigt',
  [WorkOrderStatus.CANCELLED]: 'Storniert'
};

export const WORK_ORDER_STATUS_COLORS: Record<WorkOrderStatus, string> = {
  [WorkOrderStatus.OPEN]: '#9E9E9E',
  [WorkOrderStatus.SCHEDULED]: '#FF9800',
  [WorkOrderStatus.IN_PROGRESS]: '#2196F3',
  [WorkOrderStatus.DONE]: '#4CAF50',
  [WorkOrderStatus.CANCELLED]: '#F44336'
};

export interface RolloutWorkOrder {
  id?: string;
  createdTime?: number;
  tenantId?: string;
  rolloutProjectId?: string;
  partnerId: string;
  partnerName?: string;
  serviceType: string;
  status?: WorkOrderStatus;
  title?: string;
  description?: string;
  meterIds: string[];
  scheduledDate?: number;
  completedAt?: number;
  completedBy?: string;
  notes?: string;
}

export interface MeterWorkOrderInfo {
  workOrderId: string;
  partnerName?: string;
  serviceType: string;
  status: string;
  scheduledDate?: number;
}

// =========================================================
// RFQs (Offertanfragen) & Partner Offers
// =========================================================

export enum RfqStatus {
  SENT = 'SENT',
  AWARDED = 'AWARDED',
  CANCELLED = 'CANCELLED'
}

export const RFQ_STATUS_LABELS: Record<RfqStatus, string> = {
  [RfqStatus.SENT]: 'Offen',
  [RfqStatus.AWARDED]: 'Vergeben',
  [RfqStatus.CANCELLED]: 'Storniert'
};

export enum RfqOfferStatus {
  REQUESTED = 'REQUESTED',
  SUBMITTED = 'SUBMITTED',
  DECLINED = 'DECLINED',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED'
}

export const RFQ_OFFER_STATUS_LABELS: Record<RfqOfferStatus, string> = {
  [RfqOfferStatus.REQUESTED]: 'Angefragt',
  [RfqOfferStatus.SUBMITTED]: 'Offerte eingereicht',
  [RfqOfferStatus.DECLINED]: 'Abgelehnt',
  [RfqOfferStatus.ACCEPTED]: 'Zuschlag',
  [RfqOfferStatus.REJECTED]: 'Nicht berücksichtigt'
};

export const RFQ_OFFER_STATUS_COLORS: Record<RfqOfferStatus, string> = {
  [RfqOfferStatus.REQUESTED]: '#9E9E9E',
  [RfqOfferStatus.SUBMITTED]: '#2196F3',
  [RfqOfferStatus.DECLINED]: '#795548',
  [RfqOfferStatus.ACCEPTED]: '#4CAF50',
  [RfqOfferStatus.REJECTED]: '#F44336'
};

export interface RfqPosition {
  id: string;
  description: string;
  quantity: number;
  unit: string;
}

export interface RfqPositionPrice {
  positionId: string;
  unitPrice: number;
  total: number;
}

export interface RolloutRfqOffer {
  id?: string;
  createdTime?: number;
  rfqId?: string;
  partnerId: string;
  partnerName?: string;
  status: RfqOfferStatus;
  totalAmount?: number;
  currency?: string;
  positionPrices?: RfqPositionPrice[];
  notes?: string;
  validUntil?: number;
  submittedAt?: number;
}

export interface RolloutRfq {
  id?: string;
  createdTime?: number;
  rolloutProjectId?: string;
  title: string;
  description?: string;
  serviceType: string;
  status?: RfqStatus;
  meterIds: string[];
  positions: RfqPosition[];
  dueDate?: number;
  awardedOfferId?: string;
  offers?: RolloutRfqOffer[];
}

export interface CreateRfqRequest {
  title: string;
  description?: string;
  serviceType: string;
  meterIds: string[];
  positions?: RfqPosition[];
  dueDate?: number;
  partnerIds: string[];
}

export interface RolloutOrder {
  orderId: string;
  orderNumber: string;
  status: string;
  totalAmount: number;
  currency: string;
  itemCount: number;
  createdTime: number;
  supplierName: string;
  supplierHasEmail: boolean;
  poSentAt?: number;
}

// =========================================================
// AUTO STATUS ADVANCEMENT (A3)
// =========================================================

export interface AutoAdvanceChange {
  meterId: string;
  name: string;
  buildingName: string;
  fromStatus: string;
  toStatus: string;
  reason: string;
}

export interface AutoAdvanceSuggestion {
  meterId: string;
  name: string;
  buildingName: string;
  suggestedStatus: string;
  reason: string;
}

export interface AutoAdvanceResult {
  projectId: string;
  applied: boolean;
  advanced: number;
  changes: AutoAdvanceChange[];
  suggestions: AutoAdvanceSuggestion[];
}
